import type { MemberListItem, MemberPlotSummary, Role } from '@riverpark/types'
import { plotDevelopmentStatusLabel } from '@riverpark/utils/plot-development-status'
import { formatKES } from './auth'

export function formatLastActive(ts: number | null): string {
  if (!ts) return 'Never'
  const date = new Date(ts * 1000)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  return date.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Positive = owes, negative = in credit. */
export function formatBalance(balance: number | null): { label: string; className: string } {
  if (balance != null && balance > 0) {
    return { label: `${formatKES(balance)} due`, className: 'text-status-overdue-text font-medium' }
  }
  if (balance != null && balance < 0) {
    return { label: `${formatKES(-balance)} credit`, className: 'text-status-paid-text' }
  }
  return { label: formatKES(0), className: 'text-status-paid-text' }
}

function roleLabel(role: Role): string {
  const labels: Record<Role, string> = {
    admin: 'Admin',
    committee: 'Committee',
    estate_manager: 'Estate manager',
    member: 'Member',
  }
  return labels[role]
}

export function roleBadgeVariant(role: Role): 'admin' | 'committee' | 'estate_manager' | 'member' {
  if (role === 'admin') return 'admin'
  if (role === 'committee') return 'committee'
  if (role === 'estate_manager') return 'estate_manager'
  return 'member'
}

/** For role === 'member', resolves to a plot-development-based label ("Developer" if any owned
 * plot has broken ground, else "Owner") instead of the generic "Member" — staff roles fall
 * through to their normal label unchanged. Defaults to "Owner" when no plot is owned yet. */
export function memberRoleLabel(
  role: Role,
  plots: Pick<MemberPlotSummary, 'developmentStatus'>[],
): string {
  if (role !== 'member') return roleLabel(role)
  const anyDeveloper = plots.some((p) => p.developmentStatus === 'developer')
  return plotDevelopmentStatusLabel(anyDeveloper ? 'developer' : 'owner')
}

export function statusLabel(status: MemberListItem['status']): string {
  const labels = {
    active: 'Active',
    inactive: 'Inactive',
    invited: 'Invited',
  }
  return labels[status]
}

export function plotDisplay(plots: MemberListItem['plots']): string {
  if (plots.length === 0) return '—'
  return plots.map((p) => p.newNumber).join(', ')
}
